import { create } from 'zustand';
import { fundingApi } from '../services/api';
import type { FundingOpportunity, FundingPipelineSummary } from '../types';

interface FundingState {
  opportunities: FundingOpportunity[];
  summary: FundingPipelineSummary | null;
  loading: boolean;
  error: string | null;
  selectedId: string | null;

  fetchOpportunities: (filters?: Record<string, string>) => Promise<void>;
  fetchSummary: () => Promise<void>;
  createOpportunity: (data: Partial<FundingOpportunity>) => Promise<FundingOpportunity>;
  updateOpportunity: (id: string, data: Partial<FundingOpportunity>) => Promise<FundingOpportunity>;
  deleteOpportunity: (id: string) => Promise<void>;
  setSelectedId: (id: string | null) => void;
}

export const useFundingStore = create<FundingState>((set) => ({
  opportunities: [],
  summary: null,
  loading: false,
  error: null,
  selectedId: null,

  fetchOpportunities: async (filters) => {
    set({ loading: true, error: null });
    try {
      const opportunities = await fundingApi.list(filters);
      set({ opportunities, loading: false });
    } catch (err: any) {
      set({ error: err.message, loading: false });
    }
  },

  fetchSummary: async () => {
    try {
      const summary = await fundingApi.summary();
      set({ summary });
    } catch (err: any) {
      set({ error: err.message });
    }
  },

  createOpportunity: async (data) => {
    const opportunity = await fundingApi.create(data);
    set((s) => ({ opportunities: [...s.opportunities, opportunity] }));
    return opportunity;
  },

  updateOpportunity: async (id, data) => {
    const updated = await fundingApi.update(id, data);
    set((s) => ({
      opportunities: s.opportunities.map((o) =>
        o._id === id ? { ...o, ...updated } : o
      ),
    }));
    return updated;
  },

  deleteOpportunity: async (id) => {
    await fundingApi.delete(id);
    set((s) => ({
      opportunities: s.opportunities.filter((o) => o._id !== id),
      selectedId: s.selectedId === id ? null : s.selectedId,
    }));
  },

  setSelectedId: (id) => set({ selectedId: id }),
}));
